import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAlerts } from "common";
import { API_URLS, API_STATUS } from "app/shared/constants";
import { OIS_FILTER_OPTIONS, UPLOAD_ENVIRONMENTS } from "./constants";
import { formatISODate } from "app/shared/utils";

const OISApi = () => {
  const { ALERT_TYPE, setAlert, clearAlert } = useAlerts();
  const { t } = useTranslation();

  const api = {
    /**
     * Gets OIS filter options
     */
    getOISMetadata: async () => {
      try {
        clearAlert();

        const metadata = {
          data: OIS_FILTER_OPTIONS,
        };

        if (metadata?.data) {
          return metadata.data;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.OISMetaNotFound"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.OISMetaNotFound"), e);
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Gets upload environments
     */
    getUploadEnvironments: async () => {
      try {
        clearAlert();

        const metadata = {
          data: UPLOAD_ENVIRONMENTS,
        };

        if (metadata?.data) {
          return metadata.data;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.UploadEnvironmentsNotFound"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.UploadEnvironmentsNotFound"), e);
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Fetches recent activity records.
     */
    getOISRecentActivity: async () => {
      try {
        clearAlert();

        const response = await axios.get(API_URLS.getRecentActivity);
        if (response?.data) {
          return response.data;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.OISDataFailed"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.OISDataFailed"), e);
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Extracts eProtocol activity for a given date range.
     *
     * @param {string} startDate YYYY-MM-DD
     * @param {string} endDate YYYY-MM-DD
     */
    getEProtocolActivity: async (startDate, endDate) => {
      try {
        clearAlert();
        const response = await axios.get(
          API_URLS.getEProtocolActivity(
            formatISODate(startDate),
            formatISODate(endDate),
          ),
        );

        if (response?.data) {
          return response.data;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.eProtocolActivityFailed"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.eProtocolActivityFailed"), e);
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Uploads a file to OnCore via a specific URL.
     *
     * @param {string} csvType eProtocol, participants, training
     * @param {string} instance OnCore environment
     * @param {string} uploadLink Dynamic upload link from eProtocol
     * @param {FormData} formData Must contain file except for eProtocol
     */
    uploadToOnCore: async (csvType, instance, uploadLink, formData) => {
      let url;
      switch (csvType) {
        case t("csvTypes.eProtocol"):
          url = uploadLink.replace("{instance}", instance);
          break;
        case t("csvTypes.participants"):
          url = API_URLS.importParticipantRegistration(instance);
          break;
        case t("csvTypes.training"):
          url = API_URLS.importTrainingData(instance);
          break;
        default:
          url = API_URLS.jsonPlaceholder(csvType);
          break;
      }

      try {
        clearAlert();
        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response?.status === 200 || response?.status === 201) {
          return API_STATUS.API_SUCCESS;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.uploadToOnCoreFailed"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.uploadToOnCoreFailed"), e);
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Downloads a file from the API.
     *
     * @param {string} link The download URL
     */
    downloadFile: async (link) => {
      try {
        setAlert(
          ALERT_TYPE.INFO,
          t("messages.info.api.downloadFileProcessing"),
          true,
        );

        const response = await axios({
          url: link,
          method: "GET",
          responseType: "blob",
        });

        if (response?.data) {
          const contentDisposition = response.headers["content-disposition"];
          const filenameMatch = /filename="(.+)"/.exec(contentDisposition);
          const filename = filenameMatch ? filenameMatch[1] : "download.csv";

          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(response.data);
          a.setAttribute("download", filename);
          a.click();

          return API_STATUS.API_SUCCESS;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.downloadFileFailed"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.message);
        console.error(t("messages.error.api.downloadFileFailed"), e);
        return API_STATUS.API_ERROR;
      }
    },
  };

  return api;
};

export default OISApi;
